import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './CandidateReportsList.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.avtechbmt.cloud/api/candidates';

const FRIENDLY_LABELS = {
  Understanding: 'Understanding',
  coherence: 'Coherence',
  connection: 'Connection',
  clarification: 'Clarification',
  concision: 'Concision',
  conscious: 'Awareness',
  confidence: 'Confidence',
};

const scoreTone = (v) => (v >= 4 ? 'high' : v === 3 ? 'mid' : v > 0 ? 'low' : 'empty');

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function CandidateReportsList({
  candidate,
  onBack,
  onOpenReport,
  onReportsChanged,
}) {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const getAuthHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(
        `${API_BASE_URL}/${candidate._id}/reports`,
        { headers: getAuthHeaders() }
      );
      
      const contentType = res.headers.get('content-type') || '';
      const textData = await res.text();
      
      if (!res.ok || !contentType.includes('application/json')) {
        throw new Error(
          textData.startsWith('<!DOCTYPE') || textData.includes('<html')
            ? `Server returned HTML (${res.status}). Check backend API URL.`
            : `API error (${res.status}): ${textData}`
        );
      }
      
      const data = JSON.parse(textData);
      if (!data.success) throw new Error(data.message || 'Failed to load reports');
      setReports(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [candidate._id, getAuthHeaders]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const createReport = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/${candidate._id}/reports`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          // No questionSet sent anymore
          body: JSON.stringify({}), 
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to create report');
      }
      await fetchReports();
      if (onReportsChanged) onReportsChanged();
      if (onOpenReport) onOpenReport(data.data._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteReport = async (reportId) => {
    setConfirmDeleteId(null);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/reports/${reportId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete report');
      }
      await fetchReports();
      if (onReportsChanged) onReportsChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="report-list-page">
      <header className="rl-toolbar">
        <div className="rl-toolbar-left">
          <button
            type="button"
            className="cv-btn cv-btn-ghost"
            onClick={onBack}
          >
            ← Back to Candidates
          </button>
          <div>
            <h1 className="rl-title">
              {candidate.name || 'Unnamed Candidate'}
            </h1>
            <p className="rl-subtitle">
              {reports.length} {reports.length === 1 ? 'report' : 'reports'} on
              file
            </p>
          </div>
        </div>
        <div className="rl-toolbar-right">
          <button
            type="button"
            className="cv-btn cv-btn-primary"
            onClick={createReport}
            disabled={creating}
          >
            {creating ? 'Creating…' : '+ New Evaluation Report'}
          </button>
        </div>
      </header>

      {error && (
        <div className="cv-banner cv-banner-error" role="alert">
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div className="rl-skeleton-list">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rl-skeleton-card" />
          ))}
        </div>
      ) : reports.length === 0 ? (
        <div className="cv-empty">
          No reports yet for this candidate. Click <strong>+ New Evaluation Report</strong>{' '}
          to create one.
        </div>
      ) : (
        <section className="rl-list">
          {reports.map((r) => {
            const scores = r.scores || {};
            const isConfirming = confirmDeleteId === r._id;
            const avg =
              typeof r.overallScore === 'number'
                ? r.overallScore.toFixed(1)
                : '0.0';
            return (
              <article key={r._id} className="rl-card">
                <div className="rl-card-head">
                  <div className="rl-card-meta">
                    <span className="rl-card-date">
                      Evaluated on: {formatDate(r.createdAt)}
                    </span>
                  </div>
                  <div className="rl-card-actions">
                    <button
                      type="button"
                      className="cv-btn cv-btn-outline"
                      onClick={() => onOpenReport(r._id)}
                    >
                      View Report →
                    </button>
                    {isConfirming ? (
                      <>
                        <button
                          type="button"
                          className="cv-confirm-yes"
                          onClick={() => deleteReport(r._id)}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="cv-confirm-no"
                          onClick={() => setConfirmDeleteId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="cv-remove-btn"
                        onClick={() => setConfirmDeleteId(r._id)}
                        aria-label="Delete report"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                <div className="rl-score-summary">
                  <div className="rl-score-block">
                    <span className="rl-score-num">{avg}</span>
                    <span className="rl-score-label">avg / 5</span>
                  </div>
                  <div className="rl-score-chips">
                    {Object.entries(FRIENDLY_LABELS).map(([key, label]) => {
                      const v = Number(scores[key]) || 0;
                      return (
                        <span
                          key={key}
                          className={`rl-chip tone-${scoreTone(v)}`}
                          title={`${label}: ${v}/5`}
                        >
                          {label}: <strong>{v}</strong>
                        </span>
                      );
                    })}
                  </div>
                </div>

                {r.remarks && (
                  <p className="rl-remarks">
                    <strong>Remarks:</strong> {r.remarks}
                  </p>
                )}
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}